import { Component } from '@angular/core';
import { MediaObserver } from "@angular/flex-layout";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { IFieldConfig } from "src/app/domain/IFieldConfig";
import { ITableResponsive } from "src/app/domain/ITableResponsive";
import { MessagesEnum } from "src/app/domain/enums/messages.enum";
import { TypeIndexEnum } from "src/app/domain/registration/enums/type-index.enum";
import { DialogConfirmComponent } from "src/app/shared/components/dialog-confirm/dialog-confirm.component";
import { GlobalUtils } from "src/app/shared/global-utils";
import { ToastService } from "src/app/shared/services/toast/toast.service";
import { AbstractCrudService } from "./abstract-crud.service";
import { filter, firstValueFrom, map, merge, of, switchMap, take, takeUntil } from 'rxjs';
import { ComponentType } from '@angular/cdk/portal';

interface ICrud {
  id?: string;
  [key: string]: any;
}
export abstract class AbstractCrudIndex<T extends ICrud> implements ITableResponsive {
  title = '';
  dataSelect: { type: TypeIndexEnum; select: boolean } = {
    type: TypeIndexEnum.CRUD,
    select: false,
  };
  is_mobile = false;
  displayedColumns: any[] = [];
  configColumns: any[] = [];
  tableButtons: any[] = [];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  length: number = 0;
  params: any = { limit: 25, page: 0, filter: '' };
  COLUMNS_TO_SHOW_MOBILE: any = [];
  COLUMNS_TO_SHOW_WEB: any = [];
  selection: any;
  isAdvancedFilterActive = false;
  lastAppliedFilter: any = null;

  constructor(
    public dialog: MatDialog,
    public service: AbstractCrudService,
    public toastService: ToastService,
    public mediaObserver: MediaObserver,
    public router: Router,
  ) {
    this.title = service.title;

    mediaObserver.asObservable().subscribe((res) => {
      this.is_mobile = res[0].mqAlias === 'sm' || res[0].mqAlias === 'xs';
      this.displayedColumns = this.is_mobile
        ? this.COLUMNS_TO_SHOW_MOBILE
        : this.COLUMNS_TO_SHOW_WEB;
    });
  }

  filterFields: IFieldConfig[];

  submitAdvancedFilter($event: any) {
    throw new Error('Method not implemented.');
  }

  loadList() {
    let params = this.params;

    this.service.listAll(params).subscribe((dataWithPagination: any) => {
      const [data, length] = dataWithPagination;
      this.length = length;

      this.dataSource = new MatTableDataSource<any>(
        data.map((item: any) => {
          item['hideEdit'] = !item.allowEdit;
          item['hideDelete'] = !item.allowDelete;
          return item;
        })
      );
    });
  }

  openDelete(element: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: 'REMOÇÃO' },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.delete(element);
        }
      },
    });
  }

  delete(element: any) {
    this.service.delete(element.id).subscribe(() => {
      this.loadList();
      this.toastService.showSuccess(MessagesEnum.SUCCESS_DELETED);
    });
  }

  changePage(event: any) {
    this.params.page = event.page;
    this.params.limit = event.limit;

    if (this.isAdvancedFilterActive && this.lastAppliedFilter) {
      this.submitAdvancedFilter({ value: this.lastAppliedFilter } as any);
    } else {
      this.loadList();
    }
  }

  filter(event: any) {
    this.params.filter = event;
    this.loadList();
  }

  onSelectionChange($event: any) {
    this.selection = $event
  }

  viewContracts($event: any) {
    console.log($event);
  }

  protected applyClientSideFilter(filterText?: string): void {
    if (!this.dataSource) return;

    const filterValue = filterText || this.params.filter;
    if (filterValue && filterValue.trim() !== '') {
      this.dataSource.filter = filterValue.toLowerCase();

      this.dataSource.filterPredicate = (data: any, filter: string) => {
        const searchText = filter.toLowerCase();

        return Object.keys(data).some(key => {
          const value = data[key];
          if (value === null || value === undefined) return false;

          if (typeof value === 'string') {
            return value.toLowerCase().includes(searchText);
          } else if (typeof value === 'number') {
            return value.toString().includes(searchText);
          }
          return false;
        });
      };
    }
  }

  protected combineFilters(filterText: string, useAdvancedFilter: boolean = true): void {
    this.params.filter = filterText;

    if (useAdvancedFilter && this.isAdvancedFilterActive && this.lastAppliedFilter) {
      this.submitAdvancedFilter({ value: this.lastAppliedFilter });
    } else {
      this.loadList();
    }
  }

  protected saveFilterState(serviceStatic: any, localStorageKey?: string): void {
    if (serviceStatic) {
      serviceStatic.params = { ...this.params };
      serviceStatic.isAdvancedFilterActive = this.isAdvancedFilterActive;
      serviceStatic.lastAppliedFilter = this.lastAppliedFilter;
    }

    if (localStorageKey) {
      localStorage.setItem(localStorageKey, JSON.stringify(this.params));
      localStorage.setItem(`${localStorageKey}IsAdvancedFilterActive`, this.isAdvancedFilterActive.toString());
      if (this.lastAppliedFilter) {
        localStorage.setItem(`${localStorageKey}LastAppliedFilter`, JSON.stringify(this.lastAppliedFilter));
      }
    }
  }

  protected restoreFilterState(serviceStatic: any, localStorageKey?: string): void {
    let restored = false;

    if (localStorageKey) {
      const storedParams = localStorage.getItem(localStorageKey);
      const storedIsActive = localStorage.getItem(`${localStorageKey}IsAdvancedFilterActive`);
      const storedFilter = localStorage.getItem(`${localStorageKey}LastAppliedFilter`);

      if (storedParams) {
        this.params = JSON.parse(storedParams);
        restored = true;
      }

      if (storedIsActive) {
        this.isAdvancedFilterActive = storedIsActive === 'true';
      }

      if (storedFilter) {
        this.lastAppliedFilter = JSON.parse(storedFilter);
      }
    }

    if (!restored && serviceStatic) {
      if (serviceStatic.params) {
        this.params = { ...serviceStatic.params };
      }
      this.isAdvancedFilterActive = serviceStatic.isAdvancedFilterActive || false;
      this.lastAppliedFilter = serviceStatic.lastAppliedFilter || null;
    }
  }

  protected openWithUnsavedGuard<TCmp, TResult = any>(
    component: ComponentType<TCmp>,
    config: MatDialogConfig = {},
    opts?: {
      hasChanges?: (cmp: TCmp) => boolean; // se quiser sua própria regra
      onAfterClosed?: (result: TResult | undefined) => void;
      confirmData?: {
        title?: string; content?: string; noText?: string; yesText?: string;
      };
      deferInitialMs?: number; // atraso p/ capturar snapshot inicial (default: 300ms)
    }
  ): MatDialogRef<TCmp, TResult> {
    const dialogRef = this.dialog.open<TCmp, any, TResult>(component, {
      disableClose: true,
      ...config,
    });

    // --- Snapshot inicial robusto (evita depender só de 'dirty') ---
    let initialSnapshot: string | null = null;

    const snapshotForm = (cmp: any): string | null => {
      const form = cmp?.merchantForm || cmp?.form;
      if (!form) return null;
      const val = typeof form.getRawValue === 'function' ? form.getRawValue() : form.value;
      try { return JSON.stringify(val ?? null); } catch { return null; }
    };

    const captureInitial = () => {
      const cmp: any = dialogRef.componentInstance as any;
      initialSnapshot = snapshotForm(cmp);
    };

    // adia um pouco para deixar carregar CEP/UF/options etc.
    const delay = opts?.deferInitialMs ?? 300;
    setTimeout(captureInitial, delay);

    const defaultHasChanges = () => {
      const cmp: any = dialogRef.componentInstance as any;
      // se quiser ainda considerar loading:
      if (cmp?.isLoading) return false;
      if (opts?.hasChanges) return opts.hasChanges(dialogRef.componentInstance as TCmp);

      const current = snapshotForm(cmp);
      // sem snapshot ainda? não pergunta
      if (initialSnapshot === null || current === null) return false;
      return current !== initialSnapshot;
    };

    const backdrop$ = dialogRef.backdropClick();
    const esc$ = dialogRef.keydownEvents().pipe(filter(e => e.key === 'Escape'));

    merge(backdrop$, esc$)
      .pipe(
        switchMap(() => (defaultHasChanges()
          ? this.confirmClose$(opts?.confirmData)
          : of(true)
        )),
        takeUntil(dialogRef.afterClosed())
      )
      .subscribe(canClose => {
        if (canClose) dialogRef.close();
      });

    dialogRef.afterClosed().subscribe(result => {
      opts?.onAfterClosed?.(result);
    });

    return dialogRef;
  }

  private confirmClose$(data?: {
    title?: string; content?: string; noText?: string; yesText?: string;
  }) {
    const ref = this.dialog.open(DialogConfirmComponent, {
      data: {
        title: data?.title ?? 'Deseja mesmo fechar sem salvar?',
        content: data?.content ?? 'Você perderá tudo!',
        noText: data?.noText ?? 'Cancelar',
        yesText: data?.yesText ?? 'Fechar',
      },
    });
    return ref.afterClosed().pipe(
      map(res => !!res?.result)
    );
  }
}
